import React, { useState, useEffect } from 'react'
import union from 'lodash/union'
import without from 'lodash/without'
import useField from 'components/Form/useField'
import styled, { css } from 'styled-components/macro'
import { button } from 'styles/mixins/textstyles'
import { BasicButton } from 'components/layout/buttons'
import ResizeTextarea from 'react-autosize-textarea'
import { body1, caption } from 'styles/mixins/textstyles'
import * as formatter from 'components/Form/format'
import * as validations from 'components/Form/validations'
import Checkbox from 'components/Form/NewCheckbox'
import Select from 'components/Form/NewSelect'
import { RadioGroup, RadioElement } from 'components/Form/NewRadio'

const iOS = !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform)

function mergeValidations(a, b) {
  if (!a) {
    return b
  }
  if (!b) {
    return a
  }
  const aArr = Array.isArray(a) ? a : [a]
  const bArr = Array.isArray(b) ? b : [b]
  return union(bArr, aArr)
}

const Container = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: .2rem;
  border-bottom: 1px solid ${props => {
    if (props.error) {
      return props.theme.colors.errorBase
    } else if (props.focused) {
      return props.theme.colors.primaryBase
    } else {
      return props.theme.colors.greyLight
    }
  }};
`

const FieldStyle = css`
  ${body1('black')}
  width: 100%;
  // width: ${props => `${props.width || 0}px`};
  // text-align: center;
  border: none;
  border-radius: 0;
  padding: 0;
  background: none;
  line-height: 2.2rem;

  ::placeholder {
    color: ${props => props.theme.colors.greyBase};
  }

  :focus {
    outline: none;

    ::placeholder {
      color: ${props => props.theme.colors.primaryBase};
    }
  }

  &[disabled] {
    color: ${props => iOS ? 'black' : props.theme.colors.greyLight};
  }
`

const Textarea = styled(ResizeTextarea)`
  resize: none;
  ${FieldStyle}
`

const Input = styled.input`
  ${FieldStyle}
`

const HideShowLabel = styled.div`
  ${props => button(props.theme.colors.primaryBase)}

  position: absolute;
  right: 0;
  bottom: .3rem;
  cursor: pointer;
`

const LabelText = styled.label`
  ${caption('black', 'medium')}
  display: block;
  text-align: left;
  color: ${props => props.theme.colors.greyBase};
  font-size: 1.3rem;
  padding-top: ${props => props.tight ? '.2rem' : '1rem'};
  padding-bottom: .1rem;

  ${props => props.focused && css`
    color: ${props => props.theme.colors.primaryBase};
  `}

  ${props => props.error && css`
    color: ${props => props.theme.colors.errorBase};
  `}
`

const Label = props => {
  const { label, error, value, focused, tight, id, } = props
  const text = error || (value && label) || '\u00A0'
  return <LabelText focused={focused} error={error} tight={tight} id={id}>{text}</LabelText>
}

const defaultOutputFormat = ({ formatted, raw }) => raw

function handleEmptyDefault(value) {
  if (typeof value === 'undefined' || value === null) {
    return ''
  }
  return value
}

const TextField = props => {
  const { name, validate, defaultValue, outputFormat=defaultOutputFormat, activeLabel, label, type, ...rest } = props
  const [ text, setText ] = useState(formatter.applyFormat(props.format || formatter.defaultFormat, handleEmptyDefault(defaultValue)))
  const { value, setValue, setChanged, error, revalidate, id } = useField({ name, validate, defaultValue:text.raw })
  const [ hidden, setHidden ] = useState(props.password)
  const [ focused, setFocused ] = useState(false)
  const floatingLabel = !!value && (activeLabel || label)

  useEffect(() => {
    setValue(outputFormat(text))
  }, [])
  
  const handleChange = event => {
    const newText = formatter.applyFormat(props.format || formatter.defaultFormat, event.target.value)
    setText(newText)

    setValue(outputFormat(newText))
    setChanged()

    if (rest.onChange) {
      event.persist()
      rest.onChange(event)
    }
  }

  const handleFocus = event => {
    setFocused(true)
    if (rest.onFocus) {
      event.persist()
      rest.onFocus(event)
    }
  }

  const handleBlur = event => {
    setFocused(false)
    setTimeout(() => {
      revalidate()
    }, 200)
    if (rest.onBlur) {
      event.persist()
      rest.onBlur(event)
    }
  }

  const toggleHidden = () => setHidden(hidden => !hidden)

  const Comp = (type === 'textarea') ? Textarea : Input

  return (
    <Container focused={focused} error={error}>
      <Label
        id={`${id}-label`}
        focused={focused}
        label={floatingLabel}
        value={value}
        error={error}
        tight={props.tight}
      />
      <Comp
        id={id}
        {...rest}
        placeholder={label || rest.placeholder}
        type={type !== 'textarea' && hidden ? 'password' : type}
        value={text.formatted}
        error={error}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
      />
      {props.password && (
        <HideShowLabel id={'foo'} onClick={toggleHidden}>
          {hidden ? 'Show' : 'Hide'}
        </HideShowLabel>
      )}
    </Container>
  )
}

TextField.Textarea = props => <TextField
  type="textarea"
  {...props}
/>

TextField.Phone = props => <TextField
  type="tel"
  format={formatter.phoneFormat} 
  {...props}
  validate={mergeValidations(props.validate, validations.phone)}
/>

TextField.Email = props => <TextField
  format={formatter.emailFormat} 
  {...props}
  validate={mergeValidations(props.validate, validations.email)}
/>

TextField.Name = props => <TextField
  format={formatter.nameFormat} 
  {...props}
/>

TextField.Initials = props => <TextField
  format={formatter.initialsFormat} 
  {...props}
  validate={mergeValidations(props.validate, validations.minLength(2))}
/>

TextField.FourDigitCode = props => <TextField
  type="tel"
  format={formatter.fourDigitFormat}
  {...props}
  validate={mergeValidations(props.validate, [validations.minLength(4).withMessage('Must be exactly 4 digits.'), validations.maxLength(4).withMessage('Must be exactly 4 digits.')])}
/>

TextField.Tracking = props => <TextField
  format={formatter.trackingFormat}
  {...props}
  validate={mergeValidations(props.validate, [validations.minLength(16), validations.maxLength(16)])}
/>

TextField.CreditCard = props => <TextField
  type="tel"
  format={formatter.ccFormat}
  {...props}
/>

TextField.Integer = props => <TextField
  type="tel"
  format={formatter.integerFormat}
  {...props}
/>

TextField.Decimal = props => <TextField
  type="tel"
  format={formatter.decimalFormat}
  {...props}
  validate={mergeValidations(props.validate, validations.decimal)}
/>

TextField.Date = props => <TextField
  type="tel"
  activeLabel="MM DD YYYY"
  format={formatter.dateFormat}
  outputFormat={({ formatted, raw }) => {
    return formatted.replace(/\s/g, '')
  }}
  {...props}
  validate={mergeValidations(props.validate, [validations.date, validations.validDate])}
/>

TextField.MonthYear = props => <TextField
  type="tel"
  activeLabel="MM YYYY"
  format={formatter.monthYearFormat}
  outputFormat={({ formatted, raw }) => {
    return formatted.replace(/\s/g, '')
  }}
  {...props}
  validate={mergeValidations(props.validate, [validations.monthYear, validations.validMonthYear])}
/>

TextField.RadioGroup = RadioGroup
TextField.RadioElement = RadioElement
TextField.Checkbox = Checkbox
TextField.Select = Select

const Submit = props => {
  const idName = props.name ? `${props.name}-submit` : 'submit'
  const { id, allErrors, formLoading } = useField({ name: idName, defaultValue: props.value })
  const disabled = formLoading || props.disabled || !!without(Object.keys(allErrors), 'base').length
  return (
    <BasicButton id={id} mode="primary" type="submit" {...props} disabled={disabled}>{props.children || props.value}</BasicButton>
  )
}
TextField.Submit = styled(Submit)`
  margin-top: 3.2rem;
`

const Hidden = props => {
  const { name, validate, value:defaultValue, ...rest } = props
  const { value, id } = useField({ name, validate, defaultValue })
  return <input id={rest.id || id} type="hidden" name={name} value={value} {...rest} />
}
TextField.Hidden = Hidden

export default TextField